import { ProfileRelationshipManagementDto } from './dto/profile-relationship-management.dto';
import { ProfileEmailAddress } from './model/profile-email-address.model';
import { Injectable } from '@angular/core';
import { ProfileRead } from './profile-read.model';
import { ProfileMin } from './profile-min.model';
import { ApiService, PagedSearch, ApiCacheOptions, PagedResultGeneric } from '../../shared/index'; //app/shared/index barrel;
import { PagedResult as GenericPagedResult, PagedResult } from '../../shared/model/generic-paged-result.model';
import { IProfileService } from './iprofile.service';
import { ProfileAddress } from './profileAddress.model';
import { EmailAddressType } from './email-address-type.model';
import { SocialMedia } from './social-media.model';
import { AgingSummaryRow } from '../../revenue/shared/aging-summary.model';
import { NameValInteger } from '../../shared/model/name-val-integer.model';
import { ProfileMembershipInformationMin } from './profile-membership-informaiton-min.model';
import { AffProsAndAffCodeType } from './model/aff-pros-and-aff-code-type.model';
import { PhoneType } from './model/phone-type.model';
import { ProfilePhone } from './model/profile-phone.model';
import { MemberActivity } from './model/member-activity.model';
import { ProfileRelationSearch } from './profile-relation-search.model';
import { ProfileViewRecentContactsSearch } from '../profile-view/recent-contacts/recent-contacts-search.model';
import { Contact } from '../../contacts/shared/contact.model';
import { ReferralReportSummary, ReferralCount } from '../profile-view/referral-report/referral-report-summary.model';
import { ProfileBenefitActivitySummary } from '../profile-view/benefits/profile-benefit-activity-summary.model';
import { Listing } from '../../listing/shared/listing.model';
import { Billing } from '../../revenue/shared/billing/billing.model';
import { ProfileRelationDto } from './dto/profile-relation.dto';
import { ProfilePermissionDto } from './dto/profile-permission.dto';
import { ProfileRelationshipManagementModel } from './model/profile-relationship-management.model';
import { ProfileAdvancedSearchParams } from '../search/profile-advanced-search-params.model';
import { MemberActivityBulkCreateDto } from './dto/member-activity-bulk-create.dto';
import { TaskDto } from 'src/app/tasks/shared/dto/task.dto';
import { PrimaryRelationProfileViewDto } from './dto/primary-relation-profile-view.dto';
import { SocialMediaType } from './social-media-type';
import { MembershipLevelDto } from 'src/app/benefits/shared/dto/membership-level.dto';
import { ProfileAccessGuidDto } from './dto/profile-access-guid.dto';
import { ProfileAvatarInfoDto } from './dto/profile-avatar-info.dto';
import { DeletedProfileSearchDto } from 'src/app/admin/restore-profile/deleted-profile-search.dto';
import { ProfileServiceApiService } from 'src/app/shared/api/profile-service-api.service';
import { ContactSearchDto } from 'src/app/contacts/shared/contact-search.dto';
import { Entity } from 'src/app/entity/shared/entity.model';

@Injectable()
export class ProfileService implements IProfileService {
  private _cacheOptions: ApiCacheOptions;
  private _noCacheOption: ApiCacheOptions;
  public _apiService: ApiService;

  public constructor(apiService: ApiService, private _profileServiceApiService: ProfileServiceApiService) {
    this._apiService = apiService;
    this._cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();

    this._noCacheOption = new ApiCacheOptions();
    this._noCacheOption.CacheSeconds = 0;
    this._noCacheOption.DeleteCache = true;
    this._noCacheOption.DisableCaching = true;
    this._noCacheOption.DisableSlidingCache = true;
  }
  public getProfile(profileId: number, forceRefresh?: boolean): Promise<ProfileRead> {

    if (forceRefresh === true) {
      let cache = this._profileServiceApiService.getDefaultApiCacheOptionsForNoCache();
      return this._profileServiceApiService.get(`profile/v2/${profileId}`, cache);
    }

    return this._profileServiceApiService.get(`profile/v2/${profileId}`);
  }

  public getProfileMin(profileId: number): Promise<ProfileMin> {
    return this._profileServiceApiService.get('profile/v2/' + profileId.toString() + '/min');
  }

  /**
   * gets the main contact for the given profile
   * @param profileId
   */
  public getProfileMainContact(profileId: number): Promise<ProfileRead> {
    return this._apiService.get('Profile/' + profileId.toString() + '/MainContact');
  }

  /**
   * for the given profile id: get the main related org or first primarily related org profile
   * @param profileId
   */
  public getMainOrFirstRelatedOrg(profileId: number): Promise<ProfileRead> {
    return this._apiService.get('Profile/' + profileId.toString() + '/MainOrFirstRelatedOrg');
  }

  /**
   * Checks that the given profileid is primarily related to a member. One way check only
   * @param profileId
   */
  public checkIsPrimarilyRelatedToMember(profileId: number): Promise<boolean> {
    return this._apiService.get(`Profile/${profileId}/IsPrimarilyRelatedToMember`);
  }

  /*
   * Gets all active billings for a profile
   */
  public getProfileBillings(profileId: number, forceRefresh?: boolean, inactive?: boolean): Promise<Array<Billing>> {
    if (!inactive) {
      inactive = false;
    }
    const params = `?inactive=${inactive}`;
    if (forceRefresh) {
      return this._profileServiceApiService.get(`profile/v2/${profileId}/Billings${params}`, this._noCacheOption);
    } else {
      return this._profileServiceApiService.get(`profile/v2/${profileId}/Billings${params}`);
    }
  }

  /**
   * gets the billing address for the given profile
   * @param profileId
   */
  public getProfileBillingAddress(profileId: number): Promise<ProfileAddress> {
    return this._apiService
      .getPaged('Profiles/Addresses', {
        PageNumber: 0,
        PageSize: 0,
        AddressType: 'Billing',
        ProfileID: profileId,
      })
      .then(
        (addresses) => {
          return addresses && addresses.Result && addresses.Result.length ? addresses.Result[0] : null;
        },
        (err) => err
      );
  }

  public getProfileAddress(profileAddressId: number): Promise<ProfileAddress> {
    return this._apiService.get(`Profiles/Address/${profileAddressId}`);
  }

  /**
   * gets all of the the addresses for the given profile
   * @param profileId
   */
  public getAllProfileAddressed(profileId: number): Promise<Array<ProfileAddress>> {
    return this._apiService.get(`Profile/${profileId}/Addresses`);
  }

  /**
   * updates a given address
   * @param address
   */
  public updateProfileAddress(address: ProfileAddress): Promise<void> {
    return this._apiService.put(`Profiles/Address/${address.ID}`, address);
  }

  /*
   * creates an address
   * @param address
   */
  public createProfileAddres(address: ProfileAddress): Promise<number> {
    return this._apiService.post(`Profiles/Addresses`, address);
  }

  /*
   * deletes an address
   * @param addressId
   */
  public deleteProfileAddress(addressId: number): Promise<void> {
    return this._apiService.delete(`Profiles/Address/${addressId}`);
  }

  /*
   * gets all listings for a profile
   * @param profileId
   */
  public getAllProfileListings(profileId: number): Promise<Array<Listing>> {
    return this._apiService.get(`Profile/${profileId}/Listings`);
  }

  public getMemberIconColor(profileId: number): Promise<string> {
    return this._apiService.get('Membership/' + profileId.toString() + '/MemberTypeIconColor');
  }

  /**
   * search email address types
   */
  public searchEmailTypes(search: PagedSearch): Promise<GenericPagedResult<Array<EmailAddressType>>> {
    return this._apiService.getPaged('Profiles/EmailAddressTypes', search);
  }

  public getProfileSocialMediaInfo(id: number): Promise<Array<SocialMedia>> {
    return this._profileServiceApiService.get(`/profile/v2/${id}/SocialMedias`);
  }

  public getProfileAgingSummary(id: number, forceRefresh?: boolean): Promise<Array<AgingSummaryRow>> {
    return this._profileServiceApiService.get(`profile/v2/${id}/Aging`);
  }

  public getProfileInvoiceHistory(id: number, forceRefresh?: boolean): Promise<Array<NameValInteger>> {
    return this._profileServiceApiService.get(`profile/v2/${id}/InvoiceHistory`);
  }

  public getProfileAnnualDues(id: number, forceRefresh?: boolean): Promise<number> {
    return this._profileServiceApiService.get(`profile/v2/${id}/AnnualDues`);
  }

  public getProfileTotalAmountDue(id: number, forceRefresh?: boolean): Promise<number> {
    let url = `Profile/${id}/TotalAmountDue`;

    if (forceRefresh) {
      return this._apiService.get(url, this._noCacheOption);
    } else {
      return this._apiService.get(url, this._cacheOptions);
    }
  }

  /**
   * create a profile
   * @param profile profile information to use to create
   */
  public createProfile(profile: ProfileRead): Promise<number> {
    // create a basic non-member profile. If we need something more complex, consider the AddProspects endpoint.
    let url = `/profile`;
    return this._apiService.post(url, profile).then((id: number) => {
      return this._apiService.put(`/profile/${id}/UpdateInheritedProfiles`, null).then(() => {
        return id;
      });
    });
  }

  /**
   * delete a profile
   */
  deleteProfile(id: number) {
    return this._apiService.delete(`/profile/${id}`);
  }

  public updateProfile(profile: ProfileRead): Promise<void> {
    let url = `/profile/${profile.ProfileId}`;
    return this._apiService.put(url, profile).then(() => {
      return this._apiService.put(`/profile/${profile.ProfileId}/UpdateInheritedProfiles`, null);
    });
  }

  /**
   * gets a lite object containing a profile's membership information
   */
  public getProfileMembershipInfoMin(profileId: number, forceRefresh: boolean = false): Promise<ProfileMembershipInformationMin> {
    let url = `/profile/${profileId}/membershiplevelmin`;
    if (forceRefresh) {
      return this._apiService.get(url, this._noCacheOption);
    } else {
      return this._apiService.get(url, this._cacheOptions);
    }
  }

  /**
   * change the profile's membership level from one level to another
   * @param profileId
   * @param oldMembershipLevelId
   * @param newMembershipLevelId
   */
  public changeProfileMembershipLevel(profileId: number, oldMembershipLevelId: number, newMembershipLevelId: number): Promise<void> {
    let url = `Profile/${profileId}/MembershipLevel/${oldMembershipLevelId}/${newMembershipLevelId}`;
    return this._apiService.put(url, null);
  }

  public setMemberSinceDate(profileId: number, memberSince: Date) {
    let url = `profile/${profileId}/member/membersince`;
    return this._apiService.put(url, memberSince);
  }

  public setMemberSource(profileId: number, memberSource: string) {
    return this._apiService.put(`profile/${profileId}/member/membersource`, memberSource, true);
  }
  public setMemberNotes(profileId: number, memberNotes: string) {
    return this._apiService.put(`profile/${profileId}/member/membernotes`, memberNotes, true);
  }

  public getProfileAffCodesByAffCodeType(profileId: number, forceRefresh?: boolean): Promise<Array<AffProsAndAffCodeType>> {
    let url = `profile/v2/${profileId}/AffsByAffCodeType`;

    if (forceRefresh) {
      return this._profileServiceApiService.get(url, this._noCacheOption);
    } else {
      return this._profileServiceApiService.get(url);
    }
  }

  public getProfileMemberActivity(profileId: number, forceRefresh?: boolean): Promise<Array<MemberActivity>> {
    const cacheOptions = forceRefresh ? this._apiService.getDefaultApiCacheOptionsForNoCache() : null;
    return this._profileServiceApiService.get(`profile/v2/${profileId}/MemberActivity`, cacheOptions);
  }

  public createMemberActivity(memberActivity: MemberActivity): Promise<number> {
    let url = `memberactivity`;
    return this._apiService.post(url, memberActivity);
  }

  public updateMemberActivity(memberActivity: MemberActivity): Promise<number> {
    let url = `memberactivity`;
    return this._apiService.put(url, memberActivity);
  }

  public deleteMemberActivity(memberActivityId: number): Promise<void> {
    return this._apiService.delete(`MemberActivity/${memberActivityId}`);
  }

  public massCreateMemberActivity(searchParams: ProfileAdvancedSearchParams, createSettings: MemberActivityBulkCreateDto): Promise<void> {
    const url = `profiles/memberactivity/advanced`;
    return this._apiService.post(url, { SearchParams: searchParams, CreateSettings: createSettings });
  }

  public bulkCreateMemberActivity(createSettings: MemberActivityBulkCreateDto): Promise<void> {
    const url = `MemberActivity/BulkCreate`;
    return this._apiService.post(url, createSettings);
  }

  /**
   * gets all phone types
   **/
  public getPhoneTypes(): Promise<Array<PhoneType>> {
    let url = `Profiles/PhoneTypes`;
    return this._apiService
      .getPaged(url, {
        PageNumber: 0,
        PageSize: 0,
      })
      .then(
        (data) => {
          return data.Result;
        },
        (err) => err
      );
  }

  /**
   * Gets a ProfileRelation for the two provided profiles with all the fixins a developer could want
   * @param profileId
   * @param relatedProfileId
   */
  public getProfileRelation(profileId: number, relatedProfileId: number): Promise<ProfileRelationshipManagementDto> {
    const search = { ProfileId: profileId, RelProfileID: relatedProfileId } as ProfileRelationSearch;
    return this.getProfileRelationsForManagement(search).then((data) => {
      if (data.Result.length) {
        return Promise.resolve(data.Result[0]);
      } else {
        return Promise.resolve(null);
      }
    });
  }

  /**
   * Upsert a given profile relation
   * @param profileRelation
   */
  public upsertProfileRelation(profileRelation: ProfileRelationshipManagementDto): Promise<void> {
    return this._apiService.post(`Profile/Relation/Upsert`, profileRelation);
  }

  /**
   * Gets all of a profile's phones
   * @param profileId
   */
  public getProfilePhones(profileId: number): Promise<Array<ProfilePhone>> {
    let url = `/profile/${profileId}/phones`;
    return this._apiService.get(url).then((data) => {
      return data;
    });
  }

  public getProfilePhone(profilePhoneId: number): Promise<ProfilePhone> {
    return this._apiService.get(`Profiles/Phone/${profilePhoneId}`);
  }

  /*
   * creates an phone
   * @param phone
   */
  public createProfilePhone(phone: ProfilePhone): Promise<number> {
    return this._apiService.post(`Profiles/Phones`, phone);
  }

  /**
   * updates a given phone
   * @param phone
   */
  public updateProfilePhone(phone: ProfilePhone): Promise<void> {
    return this._apiService.put(`Profiles/Phone/${phone.ProfilePhoneID}`, phone);
  }

  /*
   * deletes an phone
   * @param phoneId
   */
  public deleteProfilePhone(phoneId: number): Promise<void> {
    return this._apiService.delete(`Profiles/Phone/${phoneId}`);
  }

  public getProfileViewPrimaryRelations(
    search: ProfileRelationSearch,
    forceRefresh?: boolean
  ): Promise<GenericPagedResult<Array<PrimaryRelationProfileViewDto>>> {
    return this._profileServiceApiService.postPaged(`profile/v2/ProfileRelation/search`, search);
  }

  public getProfileRelationsForManagement(
    search: ProfileRelationSearch,
    forceRefresh?: boolean
  ): Promise<GenericPagedResult<Array<ProfileRelationshipManagementDto>>> {
    if (forceRefresh) {
      return this._apiService.getPaged(`Profile/ProfileRelationsForManagement`, search, this._noCacheOption);
    } else {
      return this._apiService.getPaged(`Profile/ProfileRelationsForManagement`, search);
    }
  }

  /*
   * gets all email address types
   */
  public getEmailAddressTypes(): Promise<Array<EmailAddressType>> {
    let url = `Profiles/EmailAddressTypes`;
    return this._apiService
      .getPaged(url, {
        PageNumber: 0,
        PageSize: 0,
      })
      .then(
        (data) => {
          return data.Result;
        },
        (err) => err
      );
  }

  public getProfileEmailAddress(profileId: number, profileEmailAddressId: number): Promise<ProfileEmailAddress> {
    return this._apiService.get(`Profile/${profileId}/Email/${profileEmailAddressId}`);
  }

  /**
   * Gets email addresses for a profile
   * @param profileId
   */
  public getProfileEmailAddresses(profileId: number): Promise<ProfileEmailAddress[]> {
    let url = `Profile/${profileId}/Emails`;
    return this._apiService.get(url);
  }

  /**
   * Creates an email address for a profile.
   * @param email
   */
  public createProfileEmailAddress(email: ProfileEmailAddress) {
    return this._apiService.post(`Profile/${email.ProfileID}/Email`, email);
  }

  /**
   * Updated an email for a profile.
   * @param email
   */
  public updateProfileEmailAddress(email: ProfileEmailAddress) {
    return this._apiService.put(`Profile/${email.ProfileID}/Email/${email.ProfileEmailAddressID}`, email);
  }

  /**
   * Deletes an email address for a profile.
   * @param email
   */
  public deleteProfileEmailAddress(email: ProfileEmailAddress) {
    return this._apiService.delete(`Profile/${email.ProfileID}/Email/${email.ProfileEmailAddressID}`);
  }

  public getProfileEventActivity(profileId: number, forceRefresh?: boolean): Promise<any> {
    // no more force refresh as services are always live at the moment.
    return this._profileServiceApiService.get(`profile/v2/${profileId}/EventActivity`);
  }

  public getProfileEventAttendees(profileId: number): Promise<Array<any>> {
    return this._apiService.get(`Profile/${profileId}/Attendees`);
  }
  public getProfileRecentContacts(
    search: ProfileViewRecentContactsSearch,
    forceRefresh?: boolean
  ): Promise<GenericPagedResult<Array<Contact>>> {
    if (forceRefresh) {
      return this._profileServiceApiService.get(`profile/v2/${search.ProfileId}/RecentContacts`, this._noCacheOption);
    } else {
      return this._profileServiceApiService.get(`profile/v2/${search.ProfileId}/RecentContacts`);
    }
  }

  public getSocialMediaTypes(): Promise<SocialMediaType[]> {
    let url = 'Profiles/SocialMediaTypes';
    return this._apiService
      .getPaged(url, {
        PageNumber: 0,
        PageSize: 0,
      })
      .then(
        (data) => {
          return data.Result;
        },
        (err) => err
      );
  }

  public getProfileSocialMedia(profileId: number): Promise<Array<SocialMedia>> {
    return this._apiService.get(`Profile/${profileId}/SocialMedias`);
  }

  public createSocialMedia(socialMedia: SocialMedia) {
    return this._apiService.post('Profile/SocialMedias', socialMedia);
  }

  public updateSocialMedia(socialMedia: SocialMedia) {
    return this._apiService.put(`Profiles/SocialMedia/${socialMedia.SocialMediaID}`, socialMedia);
  }

  public deleteSocialMedia(socialMediaId: number) {
    return this._apiService.delete(`Profiles/SocialMedia/${socialMediaId}`);
  }

  public getProfileViewReferralReport(profileId: number, forceRefresh?: boolean): Promise<ReferralReportSummary> {
    if (forceRefresh) {
      return this._profileServiceApiService.get(`profile/v2/${profileId}/ProfileViewReferralSummary`, this._noCacheOption);
    } else {
      return this._profileServiceApiService.get(`profile/v2/${profileId}/ProfileViewReferralSummary`);
    }
  }

  public getProfileViewBenefitsSummary(profileId: number, forceRefresh?: boolean): Promise<ProfileBenefitActivitySummary> {
    return this._profileServiceApiService.get(`profile/v2/${profileId}/BenefitSummary`);
  }

  public sendPortalPasswordResetEmail(email: string) {
    return this._apiService.post('profiles/portal/credential/reset/request', {
      email,
    });
  }

  public updateProfileRelation(profileRelation: ProfileRelationDto) {
    return this._apiService.put(`ProfileRelation/${profileRelation.RelationID}`, profileRelation);
  }

  public deleteProfileRelation(relationId: number) {
    return this._apiService.delete(`ProfileRelation/${relationId}`);
  }

  public createProfilePermission(profilePermission: ProfilePermissionDto) {
    return this._apiService.post('ProfilePermissions', profilePermission);
  }

  public deleteProfilePermission(profilePermission: ProfilePermissionDto) {
    return this._apiService.delete(`ProfilePermission/${profilePermission.Id}`);
  }

  public getProfileContacts(
    profileId: number,
    startDate: Date,
    endDate: Date,
    pageNumber: number,
    forceRefresh?: boolean
  ): Promise<PagedResultGeneric<Contact>> {
    const strStartDate = `${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}`;
    const strEndDate = `${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()} 11:59.99`;
    const cacheOptions = forceRefresh ? this._apiService.getDefaultApiCacheOptionsForNoCache() : null;
    return this._apiService.get(
      // tslint:disable-next-line: max-line-length
      `Contacts?ProfileId=${profileId}&StartDate=${strStartDate}&EndDate=${strEndDate}&PageNumber=${pageNumber}&OrderBy=ContactDate:desc&PageSize=5&IncludeContactsFromEmailTool=true`,
      cacheOptions
    );
  }

  public CreateProfileContact(contact: Contact) {
    return this._apiService.post(`Contacts`, contact);
  }

  public UpdateProfileContact(contact: Contact) {
    return this._apiService.put(`Contact/${contact.ContactId}`, contact);
  }

  public DeleteProfileContact(contact: Contact) {
    return this._apiService.delete(`Contact/${contact.ContactId}`);
  }

  public getInvoiceDefaults(profileId: number) {
    return this._apiService.get(`Profile/${profileId}/InvoiceDefaults`);
  }

  public getCalculateProfileArc(profileId: number, arcId: number): Promise<number> {
    return this._apiService.get(`Profile/${profileId}/AutoRevCalc/${arcId}/Amount`);
  }

  public assignMembershipLevel(profileIds: Array<number>, membershipLevelId: number) {
    return this._apiService.post(`MembershipLevel/${membershipLevelId}/AssignToProfiles`, { Entity: profileIds });
  }

  public assignMembershipLevelAdvanced(searchParams: ProfileAdvancedSearchParams, membershipLevelId: number) {
    return this._apiService.post(`MembershipLevel/${membershipLevelId}/AssignToProfiles/Advanced`, searchParams);
  }

  public assignProfilesUser(profileIds: Array<number>, userId: number) {
    return this._apiService.post(`Profiles/AssignToUser/${userId}`, profileIds);
  }

  public assignProfilesUserAdvanced(searchParams: ProfileAdvancedSearchParams, userId: number) {
    return this._apiService.post(`Profiles/AssignToUser/${userId}/Advanced`, searchParams);
  }

  public setProfilesStatusAdvanced(searchParams: ProfileAdvancedSearchParams, statusId: number) {
    return this._apiService.post(`Profiles/SetStatus/${statusId}/Advanced`, searchParams);
  }

  public setProfilesStatus(profileIds: number[], statusId: number) {
    return this._apiService.post(`Profiles/SetStatus/${statusId}`, profileIds);
  }

  public setProfilesProspectAdvanced(searchParams: ProfileAdvancedSearchParams) {
    return this._apiService.post(`Profiles/SetProspect/Advanced`, searchParams);
  }

  public setProfilesProspect(profileIds: number[]) {
    return this._apiService.post(`Profiles/SetProspect`, profileIds);
  }

  public createProfilesContact(profileIds: number[], contact: Contact): Promise<void> {
    return this._apiService.post(`Profiles/CreateContact`, { ProfileIds: profileIds, Contact: contact });
  }

  public createProfilesContactAdvanced(searchParams: ProfileAdvancedSearchParams, contact: Contact): Promise<void> {
    return this._apiService.post(`Profiles/CreateContact/Advanced`, { SearchParams: searchParams, Contact: contact });
  }

  public createProfilesTasks(profileIds: number[], task: TaskDto): Promise<void> {
    return this._apiService.post(`Tasks/CreateTasks`, { ProfileIds: profileIds, Task: task });
  }

  public createProfilesTasksAdvanced(searchParams: ProfileAdvancedSearchParams, task: TaskDto): Promise<void> {
    return this._apiService.post(`Tasks/CreateTasks/Advanced`, { SearchParams: searchParams, Task: task });
  }

  public getProfileNotes(profileId: number, forceRefresh?: boolean): Promise<string> {
    if (forceRefresh) {
      return this._apiService.get(`Profile/${profileId}/Notes`, this._noCacheOption);
    } else {
      return this._apiService.get(`Profile/${profileId}/Notes`);
    }
  }

  public upsertProfileNotes(profileId: number, notes: string): Promise<void> {
    return this._apiService.post(`Profile/${profileId}/Notes`, notes);
  }

  public mergeProfiles(keptProfileId: number, deletedProfileId: number): Promise<void> {
    return this._apiService.put('/ProfileMerge', { MasterProfileId: keptProfileId, ChildProfileId: deletedProfileId });
  }

  public getAutoAssignedMembershipLevel(profileId: number): Promise<MembershipLevelDto> {
    return this._apiService.get(`Profile/${profileId}/AutoAssignedMembershipLevel`);
  }

  public autoAssignedMembershipLevel(profileId: number): Promise<void> {
    return this._apiService.post(`Profile/${profileId}/AutoAssignedMembershipLevel`, null);
  }

  public getProfileAccessGuid(profileId: number): Promise<ProfileAccessGuidDto> {
    return this._apiService.get(`Profile/${profileId}/AccessGuid`, null, true);
  }

  public getProfileEntities(profileId: number): Promise<Array<Entity>> {
    return this._apiService.get(`Profile/${profileId}/entityList`, this._noCacheOption, true);
  }

  public getProfileAvatarInfo(profileId: number, ignoreCache?: boolean): Promise<ProfileAvatarInfoDto> {
    const cacheOptions = ignoreCache
      ? this._apiService.getDefaultApiCacheOptionsForNoCache()
      : this._apiService.getDefaultApiCacheOptionsForFrequentlyChangedData();
    return this._apiService.get(`Profile/${profileId}/AvatarInfo`, cacheOptions);
  }
  public getDeletedProfiles(searchParams: DeletedProfileSearchDto) {
    return this._apiService.get(
      `Profiles/Deleted?pagesize=${searchParams.PageSize}&PageNumber=${searchParams.PageNumber}&OrderBy=${searchParams.OrderBy}`
    );
  }
  public getDeletedProfileMainContact(profileId: number) {
    return this._apiService.get(`Profile/${profileId}/Deleted/MainContact`);
  }
  public restoreDeletedProfile(profileId: number) {
    return this._apiService.get(`Profile/${profileId}/Restore`);
  }

  public getAutoAssignPreview(profileId: number, formula: string): Promise<MembershipLevelDto> {
    return this._apiService.post(`Profile/${profileId}/PreviewAutoAssignedMembershipLevel`, formula);
  }

  public exportContactRecords(searchParams): Promise<string> {
    return this._apiService.post('profile/v2/contactrecord/export', searchParams);
  }
}
